import React from "react";

import { TextFieldProps } from "../../TextField";
import { DropdownMenuProps } from "../DropdownMenu";

export function useKeyboardEvents({
  dropdownOptions,
  onEnter,
  onSelectCallback,
  blurInput
}: {
  dropdownOptions: DropdownMenuProps["dropdownOptions"];
  onEnter?: () => void;
  onSelectCallback: TextFieldProps["onSelectOption"];
  blurInput: () => void;
}) {
  const iconButtonRef = React.useRef<HTMLElement>(null);
  const [focusedOptionIdx, setFocusedIdx] = React.useState<number | null>(null);
  const [composing, setComposing] = React.useState(false);
  const handleCompositionStart = () => setComposing(true);
  const handleCompositionEnd = () => setComposing(false);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      // composing events happen when users are typing in non-latin characters
      // skip following events when still in composing state
      if (composing) {
        return;
      }
      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp":
          if (!dropdownOptions || dropdownOptions.length === 0) {
            break;
          }

          e.preventDefault();

          if (focusedOptionIdx === null) {
            setFocusedIdx(0);
            break;
          }

          const isArrowingDown = e.key === "ArrowDown";
          const nextIdx = isArrowingDown
            ? focusedOptionIdx + 1
            : focusedOptionIdx - 1;
          const lastIdx = dropdownOptions.length - 1;

          if (nextIdx < 0) {
            setFocusedIdx(lastIdx);
          } else if (nextIdx > lastIdx) {
            setFocusedIdx(0);
          } else {
            setFocusedIdx(nextIdx);
          }
          break;
        case "Enter":
          // If no option is focused, call the onClick function of the icon button
          // This equates to a "submit" action
          if (focusedOptionIdx === null) {
            if (onEnter) {
              onEnter();
            }
            blurInput();
            break;
          }

          e.preventDefault();

          const focusedOption = dropdownOptions[focusedOptionIdx];
          if (onSelectCallback && focusedOption) {
            onSelectCallback(focusedOption);
          }
          setFocusedIdx(null);

          break;
        case "Escape":
          setFocusedIdx(null);
          blurInput();
          break;
        default:
          break;
      }
    },
    [
      focusedOptionIdx,
      dropdownOptions,
      onSelectCallback,
      blurInput,
      onEnter,
      composing
    ]
  );

  return {
    focusedOptionIdx,
    handleKeyDown,
    iconButtonRef,
    handleCompositionStart,
    handleCompositionEnd
  };
}
